import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonProps, Menu, MenuProps } from '@mantine/core';

import { CREATED } from 'constants/samplePlanning';

import { DispatchType } from 'store/dispatch/types';

import { AssignSampler } from './Modals/AssignSampler';
import { CancelSamples } from './Modals/CancelPlan';
import { ManageAlerts } from './Modals/ManageAlerts';
import { MarkNotReady } from './Modals/MarkNotReady';
import { MarkReady } from './Modals/MarkReady';

type DispatchActionsPropsType = {
  dispatchPlan: DispatchType;
  onSubmit: VoidFunction;
  buttonSize?: ButtonProps['size'];
  buttonStyles?: ButtonProps['styles'];
  menuPosition?: MenuProps['position'];
  menuWidth?: MenuProps['width'];
  menuStyles?: MenuProps['styles'];
};

const CANCEL = 'cancel';
const MARK_READY = 'mark_ready';
const MARK_NOT_READY = 'mark_not_ready';
const ASSIGN_SAMPLER = 'assign_sampler';
const SNOOZE_ALERTS = 'snooze_alerts';
const DISMISS_ALERTS = 'dismiss_alerts';

export const DispatchActions = ({
  dispatchPlan,
  onSubmit,
  buttonSize,
  buttonStyles,
  menuWidth = 200,
  menuPosition = 'bottom-end',
  menuStyles,
}: DispatchActionsPropsType) => {
  const { t } = useTranslation();
  const [menuOpened, setMenuOpened] = useState<string | null>(null);

  const handleOnSubmit = () => {
    setMenuOpened(null);
    onSubmit();
  };

  const generateMenu = () => {
    switch (menuOpened) {
      case CANCEL:
        return (
          <CancelSamples
            samplePlan={dispatchPlan}
            onSubmit={handleOnSubmit}
            onClose={() => setMenuOpened(null)}
            opened
          />
        );
      case MARK_READY:
        return (
          <MarkReady
            samplePlan={dispatchPlan}
            onSubmit={handleOnSubmit}
            onClose={() => setMenuOpened(null)}
            opened
          />
        );
      case MARK_NOT_READY:
        return (
          <MarkNotReady
            samplePlan={dispatchPlan}
            onSubmit={handleOnSubmit}
            onClose={() => setMenuOpened(null)}
            opened
          />
        );
      case ASSIGN_SAMPLER:
        return (
          <AssignSampler
            dispatchPlan={dispatchPlan}
            onSubmit={handleOnSubmit}
            onClose={() => setMenuOpened(null)}
            opened
          />
        );
      case SNOOZE_ALERTS:
      case DISMISS_ALERTS:
        return (
          <ManageAlerts
            samplePlan={dispatchPlan}
            onSubmit={handleOnSubmit}
            onClose={() => setMenuOpened(null)}
            opened
            snooze={menuOpened === SNOOZE_ALERTS}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <Menu
        shadow="md"
        width={menuWidth}
        trigger="hover"
        position={menuPosition}
        styles={menuStyles}
      >
        <Menu.Target>
          <Button variant="outline" size={buttonSize} styles={buttonStyles}>
            {t('actions')}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {Boolean(dispatchPlan.alerts.length) && (
            <>
              <Menu.Item onClick={() => setMenuOpened(SNOOZE_ALERTS)}>
                {t('snoozeAlerts')}
              </Menu.Item>
              <Menu.Item onClick={() => setMenuOpened(DISMISS_ALERTS)}>
                {t('dismissAlerts')}
              </Menu.Item>
            </>
          )}
          <Menu.Item
            disabled={dispatchPlan.status !== CREATED}
            component="a"
            target="_blank"
            href={`/zone-analysis/${dispatchPlan.operation_id}/${dispatchPlan.field_id}`}
          >
            {t('editSamplePlan')}
          </Menu.Item>
          <Menu.Item
            disabled={dispatchPlan.status !== CREATED}
            onClick={() => setMenuOpened(CANCEL)}
          >
            {t('cancel')}
          </Menu.Item>
          <Menu.Item onClick={() => setMenuOpened(ASSIGN_SAMPLER)}>{t('assignSampler')}</Menu.Item>
          <Menu.Item
            disabled={dispatchPlan.status !== CREATED || dispatchPlan.ready_to_sample}
            onClick={() => setMenuOpened(MARK_READY)}
          >
            {t('markReady')}
          </Menu.Item>
          <Menu.Item
            disabled={dispatchPlan.status !== CREATED || !dispatchPlan.ready_to_sample}
            onClick={() => setMenuOpened(MARK_NOT_READY)}
          >
            {t('markNotReady')}
          </Menu.Item>
          <Menu.Item component="a" target="_blank" href={`/orders/${dispatchPlan.operation_id}`}>
            {t('goToPlan')}
          </Menu.Item>
          <Menu.Item
            component="a"
            target="_blank"
            href={`/view-sample-status/${dispatchPlan.operation_id}/${dispatchPlan.field_id}/${dispatchPlan.id}`}
          >
            {t('viewSampleStatus')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {generateMenu()}
    </>
  );
};
